<template>
  <items-listing-layout class="project-schedule">
    <template #header-left>
      <div class="project-schedule__header-left">
        <h2 class="project-schedule__title">Scheduled Tasks</h2>
        <dropdown
          title="Project Picker"
          class="project-picker"
        >
          <slot name="button">
            <i class="ddi ddi-project" />{{ projectName }} <i class="ddi ddi-caret-down" />
          </slot>
          <template #content>
            <div>
              <ul>
                <li
                  v-for="project in projectsForSelection"
                  :key="project.id"
                  @click="handleProjectSelection(project.id)"
                >
                  <span>
                    <i class="ddi ddi-project" /> {{ project.name }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
        </dropdown>
      </div>
    </template>
    <template #header-right>
      <div class="project-schedule__header-right">
        <schedule-view-toggle v-model:view-type="viewType" />
        <dd-button
          :color="ColorAssistant.ProjectColor"
          size="medium"
          @click="goToProject()"
        >
          <span>Go to Project</span>&nbsp;
          <i class="ddi ddi-arrow-right" />
        </dd-button>
      </div>
    </template>
    <template #main-content>
      <scheduled-task-list
        v-if="viewType === 'list'"
        ref="list"
        :project-id="projectId"
        @task-click="taskClick"
        @add-task="addTaskClick"
      />
      <v-calendar
        v-else
        ref="calendar"
        :initial-page="calendarFromPage"
        :masks="{
          weekdays: 'WWW',
        }"
        :first-day-of-week="1"
        class="custom-calendar"
        disable-page-swipe
        @did-move="(pages: { month: number, year: number }[]) => handlePageUpdate(pages[0])"
      >
        <!-- The header's own arrows are hidden (see styles): schedule-header has its own -->
        <template #header-title-wrapper>
          <schedule-header
            :label="`${new Date(year, month - 1, 1).toLocaleString('default', { month: 'long' })} ${year}`"
            previous-title="Previous month"
            next-title="Next month"
            @previous="moveCalendar(-1)"
            @next="moveCalendar(1)"
            @today="goToToday"
            @add-task="addTaskClick"
          />
        </template>
        <template #day-content="{ day }">
          <div class="flex flex-col h-full z-10 overflow-hidden" style="height: 100%;" @click.exact="dayClick(day)" @click.ctrl="dayRunClick(day)">
            <span :class="{ 'is-today': day.isToday }" class="day-label">{{ day.day }}</span>
            <div class="flex-grow overflow-y-auto overflow-x-auto">
              <p
                v-for="scheduledTask in getEventsForDay(day)"
                :key="scheduledTask.id"
                class="calendar-item"
                @click.stop="taskClick(scheduledTask, new Date(day.year, day.month - 1, day.day))"
              >
                <span v-if="scheduledTask.lastRunDate == null" style="color:darkgoldenrod">&#11044;</span>
                <span v-else-if="scheduledTask.lastRunDate != null && scheduledTask.lastEventDate == null" style="color:green">&#11044;</span>
                <span v-else>&#11044;</span>
                {{ scheduledTask.name }}
              </p>
            </div>
          </div>
        </template>
      </v-calendar>
    </template>
    <template #modals>
      <!-- ADD TASK -->
      <add-scheduled-task-drawer
        v-model:show-drawer="showAddTaskDrawer"
        v-model:selected-date="selectedDate"
        v-model:existing-scheduled-task-id="existingScheduledTaskId"
        :project-id="projectId"
        @scheduled-task-created="handleScheduledTaskCreated"
        @scheduled-task-deleted="handleScheduledTaskDeleted"
      />
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import AddScheduledTaskDrawer from './components/add-scheduled-task-drawer.vue'
import ScheduledTaskList from './components/scheduled-task-list.vue'
import ScheduleViewToggle from './components/schedule-view-toggle.vue'
import ScheduleHeader from './components/schedule-header.vue'
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import LocalStore from '@/helpers/localstore'
import dayjs from '@/helpers/dayjs'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import {
  ICalendarMonth,
  IProjectForSelection,
  IScheduledTask,
} from '@/api/types/response.types'

export default defineComponent({
  name: 'ProjectSchedule',

  components: {
    ItemsListingLayout,
    MainContainer,
    AddScheduledTaskDrawer,
    ScheduledTaskList,
    ScheduleViewToggle,
    ScheduleHeader,
  },

  data() {
    const month = new Date().getMonth() + 1
    const year = new Date().getFullYear()
    return {
      ColorAssistant,
      projectName: '',
      calendarData: {} as ICalendarMonth,
      month,
      year,
      viewType:
        LocalStore.projectScheduleViewType === 'list' ? 'list' : 'calendar',
      calendarFromPage: { month, year },
      showAddTaskDrawer: false,
      projectId: parseInt(this.$route.params.id, 10),
      existingScheduledTaskId: null as number | null,
      selectedDate: new Date(),
      projectsForSelection: [] as IProjectForSelection[],
    }
  },
  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    // The open scheduled task and occurrence, as the query string that opens them (see openScheduledTaskFromRoute)
    openScheduledTaskQuery(): { scheduledTaskId?: string; date?: string } {
      if (!this.showAddTaskDrawer || this.existingScheduledTaskId == null) {
        return {}
      }
      return {
        scheduledTaskId: this.existingScheduledTaskId.toString(),
        date: dayjs(this.selectedDate).format('YYYY-MM-DD'),
      }
    },
  },
  watch: {
    // Keeps the URL pointing at the open scheduled task, so it can be shared or reloaded
    openScheduledTaskQuery(query: { scheduledTaskId?: string; date?: string }) {
      const { scheduledTaskId, date, ...otherQuery } = this.$route.query
      if (scheduledTaskId === query.scheduledTaskId && date === query.date) {
        return
      }
      this.$router.replace({ query: { ...otherQuery, ...query } })
    },
    viewType(newVal: string) {
      LocalStore.projectScheduleViewType = newVal
      if (newVal === 'calendar') {
        // Reopen the calendar on the month it last showed, with any changes made in the list
        this.calendarFromPage = { month: this.month, year: this.year }
        this.fetchScheduledTasksForMonth()
      }
    },
  },
  created() {
    this.openScheduledTaskFromRoute()
    this.fetchProjectDetails()
  },
  methods: {
    // Tasks link here with ?scheduledTaskId=&date= to open the scheduled task that created them
    openScheduledTaskFromRoute() {
      const scheduledTaskId = parseInt(
        this.$route.query.scheduledTaskId as string,
        10,
      )
      if (!scheduledTaskId) {
        return
      }
      const date = this.$route.query.date
        ? dayjs(this.$route.query.date as string, 'YYYY-MM-DD').toDate()
        : new Date()
      this.month = date.getMonth() + 1
      this.year = date.getFullYear()
      this.calendarFromPage = { month: this.month, year: this.year }
      this.selectedDate = date
      this.existingScheduledTaskId = scheduledTaskId
      this.showAddTaskDrawer = true
    },
    async fetchProjectDetails() {
      try {
        const details = await InternalProjectsAPI.get_GeneralSettings(
          this.projectId,
        )
        this.projectName = details.name

        this.projectsForSelection = await InternalProjectsAPI.get_ForSelection()

        if (!details.createScheduledTasks) {
          this.$msgbox.alert(
            'Add events here to schedule when a single or recurring task is created. ' +
              'On the event date, your task will be created automatically.',
            'Scheduled Tasks',
            {
              confirmButtonText: 'OK',
              type: 'info',
              showClose: false,
              closeOnClickModal: false,
            },
          )
        }

        // The list view loads its own events
        if (this.viewType === 'calendar') {
          this.fetchScheduledTasksForMonth()
        }
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async fetchScheduledTasksForMonth() {
      try {
        const calendarData = await InternalProjectsAPI.get_ScheduledTasksGetForMonth(
          this.projectId,
          this.year,
          this.month,
        )
        // Ignore stale responses when the month changes quickly
        if (
          calendarData.year === this.year &&
          calendarData.month === this.month
        ) {
          this.calendarData = calendarData
        }
      } catch (error) {
        this.$error.emit(error)
      }
    },
    getEventsForDay(day: any): IScheduledTask[] {
      // The grid also shows the edges of the previous and next months, which aren't in calendarData
      if (
        !this.calendarData.days ||
        day.month !== this.calendarData.month ||
        day.year !== this.calendarData.year
      ) {
        return []
      }
      const calendarDay = this.calendarData.days.find(i => i.day === day.day)
      return calendarDay ? calendarDay.scheduledTasks || [] : []
    },
    // Opens the event on the clicked occurrence's date, like other calendars do
    taskClick(scheduledTask: IScheduledTask, occurrenceDate: Date) {
      this.selectedDate = occurrenceDate
      this.existingScheduledTaskId = scheduledTask.id
      this.showAddTaskDrawer = true
    },
    dayClick(day: any) {
      // Past days are allowed; the form warns that their tasks won't be created
      this.existingScheduledTaskId = null
      this.selectedDate = new Date(day.year, day.month - 1, day.day)
      this.showAddTaskDrawer = true
    },
    async dayRunClick(day: any) {
      const now = new Date()
      const eventDate = new Date(day.year, day.month - 1, day.day)
      now.setHours(0, 0, 0, 0)
      if (eventDate <= now) {
        this.$messaging.showError('Cannot run past dates.')
        return
      }

      try {
        await InternalProjectsAPI.post_ScheduledTasksRun(
          this.projectId,
          eventDate.toDateString(),
        )
        this.$messaging.showSuccess('Scheduled task run created successfully.')
      } catch (error) {
        this.$messaging.showError(error)
      }
    },
    async handleScheduledTaskCreated(scheduledTask: IScheduledTask) {
      this.refreshScheduledTasks()
      this.showAddTaskDrawer = false
    },
    async handleScheduledTaskDeleted(scheduledTaskId: number) {
      // Reload rather than drop the event, since deleting some occurrences can keep the earlier ones
      this.refreshScheduledTasks()
      this.showAddTaskDrawer = false
    },
    refreshScheduledTasks() {
      if (this.viewType === 'list') {
        const list = this.$refs.list as any
        list.fetchPage()
      } else {
        this.fetchScheduledTasksForMonth()
      }
    },
    async handlePageUpdate(e: any) {
      this.changeMonth(e.year, e.month)
    },
    changeMonth(year: number, month: number) {
      if (month !== this.month || year !== this.year) {
        this.year = year
        this.month = month
        this.fetchScheduledTasksForMonth()
      }
    },
    addTaskClick() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      this.existingScheduledTaskId = null
      this.selectedDate = today
      this.showAddTaskDrawer = true
    },
    handleProjectSelection(projectId: number) {
      this.$router.push(
        RouteAssistant.getProjectScheduleRoute(
          this.$route.params.accountId,
          projectId,
        ),
      )
    },
    moveCalendar(months: number) {
      const calendar = this.$refs.calendar as any
      calendar.moveBy(months)
    },
    goToToday() {
      const today = new Date()
      const calendar = this.$refs.calendar as any
      calendar.move({
        month: today.getMonth() + 1,
        year: today.getFullYear(),
      })
    },
    goToProject() {
      RouteAssistant.goToProjectLanding(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
        this.$permissions,
      )
    },
  },
})
</script>

<style lang="scss">
.project-schedule {
  // The listing layout stacks its header below lg; keep this one on a row and only wrap the right side when it doesn't fit
  .items-listing__header {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px 16px;

    > :first-child {
      margin-bottom: 0;
    }
  }
  &__header-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  &__title {
    margin: 0 0 4px 0;
    font-weight: 600;
  }
  &__header-right {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(12px);
  }
  .project-picker {
    color: var(--color-text-secondary) !important;
    font-weight: 500;
    .ddi-caret-down {
      top: -1px;
      right: -2px;
      font-size: 10px !important;
    }
    .ddi-project {
      top: -1px;
      right: -1px;
      margin-right: 6px;
      color: var(--color-text-third) !important;
    }
  }
  .vc-container {
    --day-border: 1px solid #dde3ea;
    --day-border-highlight: 1px solid #b8c2cc;
    --day-width: 90px;
    --day-height: 190px;
    --weekday-bg: var(--color-background-primary);

    @include media-breakpoint-down(sm) {
      --day-width: 55px;
    }

    border-radius: 0;
    // No frame around the header and grid, like the list view
    border-top: none;
    border-left: none;
    border-right: none;
    width: 100%;

    // The schedule header has its own padding, border and month arrows
    & .vc-header {
      display: block;
      height: auto;
      padding: 0;
      margin: 0;
    }
    & .vc-header .vc-arrow {
      display: none;
    }
    & .vc-title-wrapper {
      width: 100%;
    }
    & .vc-pane {
      background-color: var(--color-background-primary);
      color: var(--color-text-secondary);
      font-weight: 500;
    }
    & .vc-weeks {
      padding: 0;
    }
    & .vc-weekday {
      padding: 5px 0;
      font-weight: normal;
      color: var(--color-text-secondary);
      background-color: var(--color-background-primary);
    }
    & .vc-day {
      cursor: pointer;
      padding: 0 5px 3px 5px;
      text-align: left;
      height: var(--day-height);
      min-width: var(--day-width);
      background-color: var(--color-background-primary);
      &:not(.on-bottom) {
        border-bottom: var(--day-border);
      }
      &:not(.on-right) {
        border-right: var(--day-border);
      }
      & .day-label {
        color: var(--color-text-secondary);
        @include media-breakpoint-down(sm) {
          font-size: 12px !important;
        }

        // Same marker as today in the list view
        &.is-today {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          align-self: flex-start;
          min-width: 24px;
          height: 24px;
          border-radius: 50%;
          color: $color--white;
          background-color: $projects--theme-color;
        }
      }
    }
    & .vc-day-dots {
      margin-bottom: 5px;
    }
    & .calendar-item {
      font-size: 13px !important;
      font-weight: 500 !important;
      padding: 5px 0 0 5px;
      color: var(--color-text-secondary);
      @include media-breakpoint-down(sm) {
        font-size: 11px !important;
      }
    }
  }
}
</style>
